'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface LiveEvent {
  id: number;
  type: 'momentum_shift' | 'injury' | 'goalie_change' | 'offensive_dominance' | 'statistical_anomaly' | 'high_volatility';
  severity: 'low' | 'medium' | 'high' | 'critical';
  game: string;
  team: string;
  player?: string;
  title: string;
  description: string;
  impact: number;
  probShift: number;
  time: string;
  isNew: boolean;
}

const INITIAL_EVENTS: LiveEvent[] = [
  { id: 1, type: 'goalie_change', severity: 'critical', game: 'EDM vs VGK', team: 'VGK', player: 'A. Hill', title: 'Changement Gardien — VGK', description: 'Adin Hill remplacé par Logan Thompson après 2 buts en 8 minutes. Impact majeur sur les probabilités.', impact: 9.1, probShift: 12.3, time: '2m ago', isNew: true },
  { id: 2, type: 'injury', severity: 'high', game: 'FLA vs NYR', team: 'NYR', player: 'A. Panarin', title: 'Blessure Possible — Panarin', description: 'Artemi Panarin quitte le banc après une mise en échec. Statut incertain pour la suite du match.', impact: 7.8, probShift: 9.4, time: '5m ago', isNew: true },
  { id: 3, type: 'momentum_shift', severity: 'high', game: 'TOR vs BOS', team: 'TOR', title: 'Momentum Shift — TOR', description: 'Toronto prend le contrôle avec 5 tirs consécutifs en 3 minutes. Pression offensive dominante.', impact: 8.4, probShift: 7.2, time: '8m ago', isNew: false },
  { id: 4, type: 'offensive_dominance', severity: 'medium', game: 'COL vs DAL', team: 'COL', title: 'Domination Offensive — COL', description: 'Colorado génère 8 chances de marquer en 5 minutes. Possession 78% dans la zone adverse.', impact: 6.2, probShift: 5.1, time: '12m ago', isNew: false },
  { id: 5, type: 'statistical_anomaly', severity: 'medium', game: 'TOR vs BOS', team: 'BOS', title: 'Anomalie Statistique — BOS', description: 'Boston 0 tirs en 8 minutes — performance historiquement basse. Détection automatique.', impact: 5.4, probShift: 3.8, time: '15m ago', isNew: false },
  { id: 6, type: 'high_volatility', severity: 'high', game: 'EDM vs VGK', team: 'EDM', title: 'Haute Volatilité Détectée', description: 'Volatilité des probabilités à 0.47 — seuil critique dépassé. Changements rapides attendus.', impact: 7.1, probShift: 8.9, time: '18m ago', isNew: false },
];

const EVENT_ICONS: Record<string, string> = {
  momentum_shift: 'BoltIcon',
  injury: 'ExclamationTriangleIcon',
  goalie_change: 'ShieldCheckIcon',
  offensive_dominance: 'FireIcon',
  statistical_anomaly: 'ChartBarIcon',
  high_volatility: 'ArrowTrendingUpIcon',
};

const EVENT_COLORS: Record<string, string> = {
  momentum_shift: 'text-primary border-primary/30 bg-primary/5',
  injury: 'text-negative border-negative/30 bg-negative/5',
  goalie_change: 'text-warning border-warning/30 bg-warning/5',
  offensive_dominance: 'text-orange-400 border-orange-400/30 bg-orange-400/5',
  statistical_anomaly: 'text-purple-400 border-purple-400/30 bg-purple-400/5',
  high_volatility: 'text-positive border-positive/30 bg-positive/5',
};

const SEVERITY_COLORS: Record<string, string> = {
  low: 'bg-info-subtle text-primary',
  medium: 'bg-warning/10 text-warning',
  high: 'bg-negative/10 text-negative',
  critical: 'bg-negative text-white',
};

export default function EventDetectionClient() {
  const [events, setEvents] = useState<LiveEvent[]>(INITIAL_EVENTS);
  const [filter, setFilter] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [newEventCount, setNewEventCount] = useState(2);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      const types: LiveEvent['type'][] = ['momentum_shift', 'injury', 'goalie_change', 'offensive_dominance', 'statistical_anomaly', 'high_volatility'];
      const games = ['TOR vs BOS', 'EDM vs VGK', 'FLA vs NYR', 'COL vs DAL'];
      const teams = ['TOR', 'BOS', 'EDM', 'VGK', 'FLA', 'NYR', 'COL', 'DAL'];
      const type = types[Math.floor(Math.random() * types.length)];
      const game = games[Math.floor(Math.random() * games.length)];
      const team = teams[Math.floor(Math.random() * teams.length)];
      const newEvent: LiveEvent = {
        id: Date.now(),
        type,
        severity: Math.random() > 0.7 ? 'high' : 'medium',
        game,
        team,
        title: `${type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} — ${team}`,
        description: `Événement détecté automatiquement pour ${team} dans ${game}.`,
        impact: Math.round(Math.random() * 5 + 4),
        probShift: Math.round(Math.random() * 8 + 2),
        time: 'just now',
        isNew: true,
      };
      setEvents((prev) => [newEvent, ...prev.slice(0, 9)]);
      setNewEventCount((c) => c + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const filtered = filter === 'all' ? events : events.filter((e) => e.type === filter);

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Event Detection Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Détection temps réel · Momentum · Blessures · Changements gardien · Anomalies</p>
        </div>
        <div className="flex items-center gap-3">
          {newEventCount > 0 && (
            <span className="status-badge bg-negative/10 text-negative border border-negative/20">
              {newEventCount} nouveaux événements
            </span>
          )}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded border transition-colors ${autoRefresh ? 'bg-positive-subtle border-positive/20 text-positive' : 'bg-card border-border text-muted-foreground'}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-positive pulse-dot' : 'bg-muted-foreground'}`} />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { type: 'all', label: 'Tous', count: events.length, color: 'text-foreground' },
          { type: 'momentum_shift', label: 'Momentum', count: events.filter((e) => e.type === 'momentum_shift').length, color: 'text-primary' },
          { type: 'injury', label: 'Blessures', count: events.filter((e) => e.type === 'injury').length, color: 'text-negative' },
          { type: 'goalie_change', label: 'Gardiens', count: events.filter((e) => e.type === 'goalie_change').length, color: 'text-warning' },
          { type: 'offensive_dominance', label: 'Domination', count: events.filter((e) => e.type === 'offensive_dominance').length, color: 'text-orange-400' },
          { type: 'statistical_anomaly', label: 'Anomalies', count: events.filter((e) => e.type === 'statistical_anomaly').length, color: 'text-purple-400' },
        ].map((cat) => (
          <button
            key={cat.type}
            onClick={() => setFilter(cat.type)}
            className={`card p-3 text-center transition-all ${filter === cat.type ? 'border-primary/50 bg-primary/5' : 'hover:border-border/80'}`}
          >
            <div className={`text-xl font-bold font-mono-data ${cat.color}`}>{cat.count}</div>
            <div className="text-2xs text-muted-foreground mt-0.5">{cat.label}</div>
          </button>
        ))}
      </div>

      {/* Events Feed */}
      <div className="space-y-3">
        {filtered.map((event) => (
          <div
            key={event.id}
            className={`card p-4 border transition-all ${EVENT_COLORS[event.type]} ${event.isNew ? 'ring-1 ring-primary/30' : ''}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${EVENT_COLORS[event.type]}`}>
                <Icon name={EVENT_ICONS[event.type] as Parameters<typeof Icon>[0]['name']} size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-semibold text-foreground">{event.title}</span>
                  {event.isNew && <span className="status-badge bg-primary/20 text-primary text-2xs">NOUVEAU</span>}
                  <span className={`status-badge text-2xs ${SEVERITY_COLORS[event.severity]}`}>{event.severity.toUpperCase()}</span>
                  <span className="ml-auto text-2xs text-muted-foreground font-mono-data">{event.time}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{event.description}</p>
                <div className="flex items-center gap-4 text-2xs">
                  <span className="text-muted-foreground">Match: <span className="text-foreground font-medium">{event.game}</span></span>
                  <span className="text-muted-foreground">Impact: <span className="text-warning font-mono-data">{event.impact}/10</span></span>
                  <span className="text-muted-foreground">Δ Prob: <span className="text-negative font-mono-data">±{event.probShift}%</span></span>
                  {event.player && <span className="text-muted-foreground">Joueur: <span className="text-foreground">{event.player}</span></span>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="card p-12 text-center">
          <Icon name="BellSlashIcon" size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucun événement pour ce filtre</p>
        </div>
      )}
    </div>
  );
}
